import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { confirmDialog } from "@/components/Confirm";
import { LocationMapEditor } from "@/components/LocationMapEditor";
import { MapPin, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function LocationsTab() {
  const [locs, setLocs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");

  const load = useCallback(async () => {
    const [l, v, u] = await Promise.all([api.get("/locations"), api.get("/vehicles"), api.get("/users")]);
    setLocs(l.data); setVehicles(v.data); setUsers(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error("Naam verplicht"); return; }
    try { await api.post("/locations", { name, code, lat: lat !== "" ? Number(lat) : null, lng: lng !== "" ? Number(lng) : null }); setName(""); setCode(""); setLat(""); setLng(""); load(); toast.success("Locatie toegevoegd"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (l) => { if (!(await confirmDialog({ title: "Locatie verwijderen", message: `"${l.name}" verwijderen?`, danger: true, confirmText: "Verwijderen" }))) return; try { await api.delete(`/locations/${l.id}`); load(); } catch (e) {} };
  const setVehLoc = async (v, lid) => { try { await api.put(`/vehicles/${v.id}`, { name: v.name, callsign: v.callsign, type: v.type, home_base: v.home_base, location_id: lid || null }); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const userLocs = (u) => u.location_ids || (u.location_id ? [u.location_id] : []);
  const toggleUserLoc = async (u, lid) => {
    const cur = userLocs(u);
    const next = cur.includes(lid) ? cur.filter((x) => x !== lid) : [...cur, lid];
    try { await api.put(`/users/${u.id}`, { location_ids: next }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const locName = (id) => locs.find((l) => l.id === id)?.name || "—";

  const pagerUsers = users.filter((u) => u.role === "meldkamer" || u.role === "beheerder");

  return (
    <div className="space-y-5" data-testid="locations-tab">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><MapPin style={{ width: 16, height: 16 }} /> Interne locaties</div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input data-testid="loc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Naam (bijv. Kazerne Noord)" className="min-w-[200px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <input data-testid="loc-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="w-32 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <input data-testid="loc-lat" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="Lat" className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <input data-testid="loc-lng" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="Lng" className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <button data-testid="loc-add-btn" onClick={add} className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700"><Plus className="h-4 w-4" /> Toevoegen</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {locs.length === 0 ? <div className="text-sm text-slate-400">Nog geen locaties.</div> : locs.map((l) => (
            <div key={l.id} data-testid={`loc-${l.id}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
              <span className="font-semibold text-slate-800">{l.name}</span>{l.code && <span className="font-mono text-xs text-slate-400">{l.code}</span>}
              {l.lat != null && <span className="font-mono text-[10px] text-emerald-600">📍 {Number(l.lat).toFixed(4)},{Number(l.lng).toFixed(4)}</span>}
              <button data-testid={`loc-del-${l.id}`} onClick={() => del(l)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <LocationMapEditor locations={locs} canWrite onChanged={load} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 text-sm font-bold text-slate-900">Voertuigen → locatie</div>
        <div className="space-y-2">{vehicles.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2">
            <div><span className="font-mono font-semibold text-slate-900">{v.callsign}</span> <span className="text-sm text-slate-500">{v.name}</span></div>
            <select data-testid={`veh-loc-${v.id}`} value={v.location_id || ""} onChange={(e) => setVehLoc(v, e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-blue-500">
              <option value="">— geen —</option>{locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        ))}</div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-1 text-sm font-bold text-slate-900">Pager-gebruikers → locatie(s)</div>
        <div className="mb-3 text-xs text-slate-400">Een geactiveerde pager hoort bij een persoon; een persoon kan aan één of meer locaties gekoppeld zijn. De pager alarmeert voor incidenten van elke gekoppelde locatie.</div>
        <div className="space-y-2">{pagerUsers.map((u) => {
          const sel = userLocs(u);
          return (
            <div key={u.id} data-testid={`user-loc-row-${u.id}`} className="rounded-lg border border-slate-100 px-3 py-2.5">
              <div className="mb-2 flex items-center gap-2">
                <span className="font-semibold text-slate-900">{u.name}</span>
                <span className="text-xs text-slate-400">{sel.length ? sel.map(locName).join(", ") : "geen locatie"}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {locs.length === 0 ? <span className="text-xs text-slate-400">Maak eerst een locatie aan.</span> : locs.map((l) => {
                  const on = sel.includes(l.id);
                  return (
                    <button key={l.id} data-testid={`user-loc-${u.id}-${l.id}`} onClick={() => toggleUserLoc(u, l.id)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}</div>
      </div>
    </div>
  );
}
